import React, { KeyboardEvent, ReactNode } from "react";
import styled, { css } from "styled-components";
import type { WebTheme } from "@ifgengineering/design-tokens/legacy";

export type V3CardPadding = "standard" | "compact" | "none";
export type V3CardElevation = "none" | "low";

export interface V3CardProps {
  children: ReactNode;
  padding?: V3CardPadding;
  elevation?: V3CardElevation;
  interactive?: boolean;
  onClick?: () => void;
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  testID?: string;
}

const paddingFor = (theme: WebTheme, padding: V3CardPadding): number => {
  if (padding === "none") return 0;
  return padding === "compact" ? theme.space.card.inset : theme.space["2xl"];
};

interface StyledCardProps {
  $padding: V3CardPadding;
  $elevation: V3CardElevation;
  $interactive: boolean;
}

const StyledCard = styled.div<StyledCardProps>`
  background: ${({ theme }) => theme.colors.ui.background.raised};
  color: ${({ theme }) => theme.colors.ui.text.primary};
  border: ${({ theme }) => theme.stroke.md}px solid
    ${({ theme }) => theme.colors.ui.border.subtle};
  border-radius: ${({ theme }) => theme.radius.md}px;
  padding: ${({ theme, $padding }) => paddingFor(theme, $padding)}px;
  box-shadow: ${({ theme, $elevation }) =>
    $elevation === "none" ? "none" : theme.elevation[$elevation]};

  ${({ $interactive }) =>
    $interactive &&
    css`
      cursor: pointer;
      transition: border-color ${({ theme }) => theme.motion.duration.instant}ms
        ${({ theme }) => theme.motion.easing.standard};

      &:hover {
        border-color: ${({ theme }) => theme.colors.ui.border.strong};
      }

      &:focus-visible {
        outline: ${({ theme }) => theme.stroke.focus}px solid
          ${({ theme }) => theme.colors.ui.border.focus};
        outline-offset: ${({ theme }) => theme.stroke.focus}px;
      }
    `}
`;

export const V3Card = ({
  children,
  padding = "standard",
  elevation = "none",
  interactive = false,
  onClick,
  as = "div",
  className,
  testID = "v3-card",
}: V3CardProps): JSX.Element => {
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (!interactive || !onClick) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onClick();
  };

  return (
    <StyledCard
      as={as}
      $padding={padding}
      $elevation={elevation}
      $interactive={interactive}
      onClick={interactive ? onClick : undefined}
      onKeyDown={handleKeyDown}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      className={className}
      data-testid={testID}
    >
      {children}
    </StyledCard>
  );
};
